import { observable, action, computed } from "mobx";
import { Period } from "@state/common/PeriodConverter";
import { TPrematchSubMenuParent as TParentMenu } from "@data-types/offer/prematch/TPrematchSubMenuParent";
import { DateTime } from "luxon";
import { localizationService } from "@state/services";
import { getCurrentCulture } from "@utils";

export class PrematchPeriodSubMenuStore {
	protected parentMenu: TParentMenu;
	previousPeriodMenu: { period: Period; name: string }[];

	constructor(parentMenu: TParentMenu) {
		this.parentMenu = parentMenu;
		this.previousPeriodMenu = this.generatePreviousPeriods();
	}

	periodMenu: { period: Period; name: string }[] = [
		{
			period: "today",
			name: "PREMATCH.SIDEMENU.PERIOD.TODAY",
		},
		{
			period: "3h",
			name: "PREMATCH.SIDEMENU.PERIOD.3H",
		},
		{
			period: "48h",
			name: "PREMATCH.SIDEMENU.PERIOD.48H",
		},
		{
			period: "3d",
			name: "PREMATCH.SIDEMENU.PERIOD.3D",
		},
		{
			period: "full",
			name: "PREMATCH.SIDEMENU.PERIOD.FULL",
		},
	];

	//#region observable

	@observable isPeriodMenuExpanded = false;
	@observable selectedPeriodKey: Period = "full";

	@computed get selectedPeriod() {
		return (
			this.periodMenu.find((i) => i.period === this.selectedPeriodKey) ||
			this.previousPeriodMenu.find(
				(i) => i.period === this.selectedPeriodKey
			) || { period: "", name: "" }
		);
	}

	//#endregion observable

	//#region period menu setters
	@action.bound
	onInitialize(period: Period) {
		this.setPeriod(period);
	}

	@action.bound
	setPeriod(period: Period) {
		if (
			this.periodMenu.find((item) => item.period === period) == null &&
			this.previousPeriodMenu.find((item) => item.period === period) ==
				null
		) {
			// Period is not valid
			return;
		}
		this.selectedPeriodKey = period;
	}

	@action.bound
	setPeriodKey(period: Period, shouldOpenOfferMenu: boolean = true) {
		if (this.selectedPeriodKey !== period) {
			this.setPeriod(period);
			this.parentMenu.fetchData();

			this.closePeriodMenu();
			if (shouldOpenOfferMenu) {
				this.parentMenu.openOfferMenu();
			}
		}
	}

	//#endregion period menu setters
	generatePreviousPeriods() {
		const pastDays = [
			{
				period: DateTime.now().toISODate(),
				name: "PREMATCH.SIDEMENU.PERIOD.FINISHED_TODAY",
			},
			{
				period: DateTime.now().minus({ days: 1 }).toISODate(),
				name: "PREMATCH.SIDEMENU.PERIOD.YESTERDAY",
			},
		];
		for (let i = 2; i <= 14; i++) {
			const date = DateTime.now().minus({ days: i });
			pastDays.push({
				period: date.toISODate(),
				name: date.setLocale(getCurrentCulture()).toFormat("EEE dd.MM"),
			});
		}
		return pastDays;
	}
	//#region menu actions

	@action.bound
	togglePeriodMenu() {
		if (this.isPeriodMenuExpanded) {
			this.closePeriodMenu();
		} else {
			this.expandPeriodMenu();
		}
	}

	@action.bound
	closePeriodMenu() {
		this.isPeriodMenuExpanded = false;
		this.parentMenu.rootStore.closeSideMenu();
	}

	@action.bound
	expandPeriodMenu() {
		this.isPeriodMenuExpanded = true;
		this.parentMenu.isOfferMenuOpen = false;
		this.parentMenu.rootStore.openSideMenu();
	}

	//#endregion menu actions
}
